
import Vue, {VueConstructor} from 'vue'
import {ListingDetailInterface} from '~/store/listing'
import QueryMixin from '~/mixins/query.mixin'
import moment from 'moment'
import FilterStateMixin from '~/mixins/filter-state.mixin'
import ResponsiveMixin from '~/mixins/responsive.mixin'
import 'swiper/css/swiper.css'

export default (Vue as VueConstructor<Vue & InstanceType<typeof FilterStateMixin> & InstanceType<typeof ResponsiveMixin> & InstanceType<typeof QueryMixin>>).extend({
  name: 'ListingShow',
  mixins: [FilterStateMixin, ResponsiveMixin, QueryMixin],
  head() {
    return {
      bodyAttrs: {
        class: 'bg-white text-offBlack'
      },
      title: this.$data.seoTitle,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.$data.seoDescriptionMeta,
        }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: this.$data.productJSON,
        }
      ],
      link: [
        {
          rel: 'canonical',
          href: this.$data.canonicalURL,
        }
      ]
    }
  },
  data(): {
    mobileMenuOpen: boolean,
    seoTitle: string,
    seoDescriptionMeta: string,
    productJSON: object,
    canonicalURL: string,
  } {
    return {
      mobileMenuOpen: false,
      seoTitle: '',
      seoDescriptionMeta: '',
      productJSON: {},
      canonicalURL: '',
    }
  },
  computed: {
    chefIdOrSlug(): string {
      return this.$route.params.id
    },
    chefId(): string {
      return (this.chef?.id ?? 0).toString(10)
    },
    chef(): ListingDetailInterface | undefined {
      return this.$store.getters['listing/byIdOrSlug'](this.chefIdOrSlug)
    },
    blockedDates(): Date[] {
      const blockedDates = this.chef?.chef.blocked_dates ?? []
      const map = blockedDates.map((date: string) => moment(date).toDate())

      if (this.chef?.booking_days_in_advance) {
        // We start at 0, so "1 day in advance" is block today
        for (let i = 0; i < this.chef.booking_days_in_advance; i++) {
          const date = moment().add(i, 'days');
          map.push(date.toDate());
        }
      }

      return map;
    }
  },
  async mounted() {
    setTimeout(() => {
      if (this.chef) {
        const price = this.chef.price_from_in_cents / 100;

        const item = {
          item_id: this.chef.id,
          item_name: this.chef.title + ' | ' + this.chef.subtitle,
          price: price * this.chef.min_guests,
          quantity: this.chef.min_guests,
        };

        if (this.filters.person > 0) {
          item.price = price * this.filters.persons;
          item.quantity = this.filters.persons;
        }

        this.$gtm.push({
          event: 'view_item',
          value: item.price,
          currency: "EUR",
          items: [
            item,
          ]
        });
      }
    }, 1000)
  },
  async fetch() {
    // Fetch chef before filters
    await this.$store.dispatch('listing/fetchListing', {
      id: this.$route.params.id,
    })

    if (!this.chef) {
      this.$nuxt.context.redirect('/')
      return;
    }

    let {
      address,
      calendar,
      person,
      dynamicFilters,
      selectedExtras
    } = await this.parseQueryParams(this.$route.query as { [key: string]: string })

    // generic function to validate/filter/clean parsed query params based on $chef?
    // e.g. min/max guests, selected date
    if (calendar) {
      const formattedDate = moment(calendar).format('YYYY-MM-DD');
      const blockedDates = this.chef?.chef.blocked_dates ?? [];
      if (blockedDates.indexOf(formattedDate) !== -1) {
        calendar = undefined;
      }
    }

    this.$store.commit('filter/UPDATE_FILTERS', {
      address,
      calendar,
      person,
      dynamicFilters
    });

    this.seoTitle = `${this.chef.title} | ${this.chef.subtitle}`;

    let arrangement = 'arrangement';
    if (this.chef.features?.listing_category?.length > 0) {
      arrangement = this.chef.features.listing_category[0] + ' arrangement';
    }

    this.seoDescriptionMeta = `Boek hier direct het ${arrangement} van ${this.chef.chef.display_name} - ${this.chef.chef.first_name} ${this.chef.chef.last_name}. Het grootste aanbod van professionele cateraars en privé chefs vind je op CHEFSTBL.`;

    // first 3 image URLs
    const images = this.chef.images.slice(0, 3).flatMap(image => image.url);

    this.productJSON = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": this.chef.title,
      "description": this.chef.subtitle,
      "image": images,
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": String(this.chef.price_from_in_cents / 100),
        "priceCurrency": "EUR"
      }
    };

    if (this.chef.review_amount > 0) {
      // @ts-ignore
      this.productJSON["aggregateRating"] = {
        "@type": "AggregateRating",
        "ratingValue": String(this.chef.review_score),
        "bestRating": "10",
        "ratingCount": String(this.chef.review_amount)
      };
    }

    this.canonicalURL = 'https://chefstbl.com/chef/' + this.chef.slug + '/';

    this.$store.commit('listing/UPDATE_SELECTED_EXTRA', {
      key: this.chefId,
      // @ts-ignore
      data: (selectedExtras ?? []).map(option => {
        return {
          id: option.id,
          value: option.quantity,
        }
      })
    });
  },
  fetchKey: 'chef-detail'
})
